import { motion } from 'framer-motion';
import {
  badgeStyle,
  toneForOpenCallStatus,
  toneForTicketStatus,
  toneForRating,
  humanize,
  actionButtonStyle,
} from '../theme/adminVisualSystem';

/**
 * Shared atoms: StatusBadge, RatingBadge, ActionButton, SectionHeader, CurrentStateLine.
 * Use these everywhere in the admin panel for visual consistency.
 */

// ─── StatusBadge ──────────────────────────────────────────────

export function StatusBadge({ status, kind = 'open_call', strong = false, className = '' }) {
  const tone =
    kind === 'ticket'
      ? toneForTicketStatus(status)
      : toneForOpenCallStatus(status);
  return (
    <span
      className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em] ${className}`}
      style={badgeStyle(tone, strong)}
    >
      {humanize(status || (kind === 'ticket' ? 'pending' : 'new'))}
    </span>
  );
}

export function RatingBadge({ rating, strong = false, className = '' }) {
  if (!rating) {
    return (
      <span
        className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em] text-[#555] border-[#222] ${className}`}
      >
        Unrated
      </span>
    );
  }
  const tone = toneForRating(rating);
  return (
    <span
      className={`inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em] ${className}`}
      style={badgeStyle(tone, strong)}
    >
      {humanize(rating)}
    </span>
  );
}

// ─── ActionButton ─────────────────────────────────────────────

/**
 * Uniform action button with variant + active/disabled state.
 *   variant: primary | secondary | success | warning | danger | purple | blue | gold | ghost | cyan
 */
export function ActionButton({
  variant = 'secondary',
  active = false,
  disabled = false,
  icon: Icon,
  children,
  className = '',
  dense = false,
  ...rest
}) {
  const sz = dense
    ? 'text-[10px] px-2.5 py-1.5'
    : 'text-[11px] px-3 py-2';
  return (
    <motion.button
      whileHover={!disabled ? { y: -1.5 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      transition={{ duration: 0.12 }}
      disabled={disabled}
      data-clickable="true"
      className={`relative inline-flex items-center justify-center gap-2 font-mono uppercase tracking-[0.14em] border rounded-none transition-all duration-200 disabled:cursor-not-allowed ${sz} ${className}`}
      style={actionButtonStyle(variant, { active, disabled })}
      {...rest}
    >
      {Icon && <Icon className={dense ? 'w-3 h-3' : 'w-3.5 h-3.5'} />}
      <span className="leading-none">{children}</span>
      {active && (
        <span
          aria-hidden
          className="absolute -top-px left-3 right-3 h-px"
          style={{ background: 'currentColor', opacity: 0.9 }}
        />
      )}
    </motion.button>
  );
}

// ─── Section header ──────────────────────────────────────────

/**
 * Panel section header with title + helper + cyan accent line.
 * Use in place of the old uppercase monospace micro-caption.
 */
export function SectionHeader({ title, helper, icon: Icon, accent = true, size = 'md', className = '' }) {
  const titleCls = size === 'lg' ? 'text-lg' : 'text-base';
  return (
    <div className={`mb-3 ${className}`}>
      <div className="flex items-center gap-2.5">
        {accent && (
          <span
            aria-hidden
            className="block w-1.5 h-1.5 rounded-full"
            style={{ background: '#90E0EF', boxShadow: '0 0 8px rgba(144,224,239,0.7)' }}
          />
        )}
        {Icon && <Icon className="w-4 h-4 text-[#90E0EF]" />}
        <h3 className={`font-outfit ${titleCls} font-semibold text-white tracking-tight`}>
          {title}
        </h3>
      </div>
      {helper && (
        <p className="text-[11px] font-mono text-[#777] mt-1 leading-relaxed">
          {helper}
        </p>
      )}
    </div>
  );
}

// ─── Current state line (below button groups) ────────────────

/**
 * Small "Current {label} · {value}" line below a button group.
 * Color-coded by tone.
 */
export function CurrentStateLine({
  label = 'Current',
  value,
  tone,
  emptyLabel = 'None',
}) {
  const resolved = value ? humanize(value) : emptyLabel;
  const color = tone?.text || '#90E0EF';
  return (
    <p className="mt-3 text-[11px] font-mono text-[#777]">
      {label}
      <span className="mx-1.5 text-[#333]">·</span>
      <span style={{ color }} className="font-medium">
        {resolved}
      </span>
    </p>
  );
}
